/**
 * Browser model store (GLOSSARY.md): the persisted copy of browser-run model
 * bytes (UForm text encoder, EmbeddingGemma) in IndexedDB, keyed by model
 * version, so the browser downloads each model from the local server only
 * once. Distinct from Cache (server-side derived container artifacts).
 *
 * Contract (issue 01):
 * - Cold miss: caller fetches over the network with determinate byte progress,
 *   then writes bytes + version key to the store.
 * - Warm hit: bytes come from IndexedDB with no network fetch; the caller
 *   shows a loading indicator (no fake determinate progress) during the store
 *   read + WASM session init.
 * - Stale entry (version key or byte-size mismatch vs the build-baked
 *   `modelManifest` expectation): treated as a miss — refetch, replace, proceed.
 * - Store unavailable/full/blocked (private mode, quota): silent memory
 *   fallback — every function here never throws, so search never hard-blocks
 *   on storage.
 */

import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { log } from "../../logger";
import { expectedSizeForKey } from "./modelManifest";

interface ModelStoreDB extends DBSchema {
  modelFiles: {
    key: string;
    value: {
      key: string;
      version: string;
      bytes: Uint8Array;
      size: number;
    };
  };
}

const DB_NAME = "egalregal-models";
const STORE = "modelFiles";

let dbPromise: Promise<IDBPDatabase<ModelStoreDB>> | null = null;

function open(): Promise<IDBPDatabase<ModelStoreDB>> {
  if (!dbPromise) {
    dbPromise = openDB<ModelStoreDB>(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: "key" });
        }
      },
    });
  }
  return dbPromise;
}

/** Test seam: drop the cached connection so `fake-indexeddb` isolation works. */
export function __resetModelStoreForTests(): void {
  dbPromise = null;
}

/**
 * Read model bytes from the store. Returns null on miss, stale entry
 * (version mismatch, or byte-size mismatch vs the explicit expectation or —
 * when omitted — the build-baked `modelManifest` size), or any storage error.
 * Callers treat null as a cold miss and fetch into memory. Never throws.
 */
export async function getModelBytes(
  key: string,
  expectedVersion: string,
  expectedSize?: number,
): Promise<Uint8Array | null> {
  try {
    const db = await open();
    const record = await db.get(STORE, key);
    if (!record) return null;
    if (record.version !== expectedVersion) {
      await db.delete(STORE, key).catch((err) => {
        log.warn({ err, key }, "[model-store] Failed to delete stale model entry (version mismatch)");
      });
      return null;
    }
    const wantSize = expectedSize ?? expectedSizeForKey(key);
    if (wantSize !== undefined && (record.size !== wantSize || record.bytes.length !== wantSize)) {
      await db.delete(STORE, key).catch((err) => {
        log.warn({ err, key }, "[model-store] Failed to delete stale model entry (size mismatch)");
      });
      return null;
    }
    return record.bytes;
  } catch (err) {
    log.warn({ err }, "[model-store] IndexedDB read failed; falling back to memory");
    return null;
  }
}

/**
 * Persist model bytes with their version key. Overwrites stale entries.
 * Silent no-op on quota/blocked storage — never throws.
 */
export async function putModelBytes(key: string, version: string, bytes: Uint8Array): Promise<void> {
  try {
    await (await open()).put(STORE, { key, version, bytes, size: bytes.length });
  } catch (err) {
    log.warn({ err }, "[model-store] IndexedDB write failed; keeping bytes in memory");
  }
}

/** Remove every stored model file. Used by tests and (if ever needed) a version purge. */
export async function clearModelStore(): Promise<void> {
  try {
    await (await open()).clear(STORE);
  } catch (err) {
    log.warn({ err }, "[model-store] IndexedDB clear failed");
  }
}
